import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Check,
  FileText,
  Link2,
  Mail,
  Plus,
  Sparkles,
  Upload,
  Vault,
  X,
} from "lucide-react";

import {
  Alert,
  Badge,
  Button,
  Card,
  Progress,
  ProgressIndicator,
  ProgressTrack,
  Separator,
} from "@/components/ui";
import { cn } from "@/lib/utils";

import { AriBrandAvatar } from "@/components/demo-ari/AriBrandIcon";
import type { AriBrandId } from "@/lib/ariBrands";
import {
  DC_OFFERS,
  DC_PARSE_STEPS,
  DC_SAMPLE_FILES,
  type SampleUploadFile,
} from "./dealComparisonData";

interface DealComparisonUploadViewProps {
  onCancel: () => void;
  onComplete: () => void;
}

export function DealComparisonUploadView({
  onCancel,
  onComplete,
}: DealComparisonUploadViewProps) {
  const [files, setFiles] = useState<SampleUploadFile[]>([]);
  const [drag, setDrag] = useState(false);
  const [stage, setStage] = useState<"select" | "parsing">("select");

  const addSampleFile = (f: SampleUploadFile) => {
    if (files.some((x) => x.id === f.id)) return;
    setFiles((prev) => [...prev, f]);
  };

  const addCustom = () => {
    const next = DC_SAMPLE_FILES.find((s) => !files.some((f) => f.id === s.id));
    if (next) addSampleFile(next);
  };

  const removeFile = (id: string) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const canAnalyze = files.length >= 2;

  if (stage === "parsing") {
    return (
      <DealComparisonParseScreen
        files={files}
        onDone={onComplete}
        onCancel={() => setStage("select")}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-6 py-3.5">
        <Button type="button" variant="outline" size="icon-sm" onClick={onCancel} aria-label="Back">
          <ArrowLeft className="size-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-semibold text-primary">
            Deal Comparison
          </p>
          <p className="text-sm font-semibold">Upload competing offers</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className={cn(
              "size-1.5 rounded-full",
              files.length >= 2 ? "bg-success" : "bg-border",
            )}
          />
          {files.length} of 2+ added
        </div>
      </header>

      <div className="flex-1 overflow-auto px-6 py-7">
        <div className="mx-auto max-w-3xl">
          <h1 className="mb-2 text-2xl font-semibold">
            Drop the offers. Equal IQ does the math.
          </h1>
          <p className="mb-6 max-w-xl text-sm leading-relaxed text-muted-foreground">
            Add 2 or more competing contracts for the same client. We extract cash, rights, term,
            exclusivity, and risk from each — then put them side by side.
          </p>

          <button
            type="button"
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              addCustom();
            }}
            onClick={addCustom}
            className={cn(
              "mb-3.5 w-full rounded-xl border border-dashed p-8 text-center transition-colors",
              drag
                ? "border-primary bg-primary/5"
                : "border-border bg-card hover:border-primary/50",
            )}
          >
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Upload className="size-5" />
            </div>
            <p className="text-sm font-semibold">Drop offer contracts or click to add</p>
            <p className="mt-1 text-xs text-muted-foreground">
              PDF, DOCX, or paste text · Encrypted at rest
            </p>
            <div className="mt-3.5 flex justify-center gap-1.5">
              {[".pdf", ".docx", ".doc", ".txt"].map((ext) => (
                <Badge key={ext} variant="outline" className="font-mono text-[10.5px]">
                  {ext}
                </Badge>
              ))}
            </div>
          </button>

          <div className="mb-3 flex items-center gap-2.5 text-[11px] text-muted-foreground">
            <Separator className="flex-1" />
            <span className="font-semibold">or pull from</span>
            <Separator className="flex-1" />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {[
              { icon: Mail, label: "Email attachments", sub: "4 unprocessed" },
              { icon: Vault, label: "Vault", sub: "Active offers folder" },
              { icon: Link2, label: "Paste a link", sub: "DocuSign · Box · Drive" },
            ].map((s) => (
              <Button
                key={s.label}
                type="button"
                variant="outline"
                className="h-auto justify-start gap-2.5 px-3 py-2.5 text-left"
                onClick={addCustom}
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-primary">
                  <s.icon className="size-3.5" />
                </div>
                <div>
                  <div className="text-xs font-semibold">{s.label}</div>
                  <div className="text-[10.5px] text-muted-foreground">{s.sub}</div>
                </div>
              </Button>
            ))}
          </div>

          <div className="mb-3.5 flex items-baseline justify-between">
            <p className="text-[11px] font-semibold text-muted-foreground">
              Queue · {files.length} {files.length === 1 ? "contract" : "contracts"}
            </p>
            {files.length === 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-dashed"
                onClick={() => setFiles(DC_SAMPLE_FILES)}
              >
                <Sparkles className="size-3.5" />
                Try with sample · Travis Scott × 3 brands
              </Button>
            )}
          </div>

          {files.length === 0 ? (
            <Card className="border-dashed p-5 text-center text-xs text-muted-foreground">
              No contracts yet. Drop files above or use the sample to see the flow.
            </Card>
          ) : (
            <div className="flex flex-col gap-2">
              {files.map((f) => {
                const offer = DC_OFFERS.find((o) => o.id === f.offerId);
                return (
                  <Card key={f.id} className="flex-row items-center gap-3 p-3">
                    {offer ? (
                      <AriBrandAvatar brandId={offer.id as AriBrandId} size="md" className="rounded-md" />
                    ) : (
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
                        <FileText className="size-3.5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="mb-0.5 flex items-center gap-2">
                        <span className="truncate text-xs font-semibold">{f.name}</span>
                        {offer && (
                          <Badge variant="secondary" className="text-[9px]">
                            {offer.brand}
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {f.pages} pages · {f.size} · From {f.from} · {f.received}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => removeFile(f.id)}
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </Card>
                );
              })}
              <Button
                type="button"
                variant="outline"
                className="border-dashed"
                disabled={files.length >= DC_SAMPLE_FILES.length}
                onClick={addCustom}
              >
                <Plus className="size-3.5" />
                {files.length >= DC_SAMPLE_FILES.length
                  ? "All sample offers added"
                  : "Add another contract"}
              </Button>
            </div>
          )}
        </div>
      </div>

      <footer className="flex shrink-0 items-center gap-3 border-t border-border bg-card px-6 py-3">
        <p className="flex-1 text-[11.5px] text-muted-foreground">
          {files.length < 2 ? (
            "Add at least 2 offers to start the comparison."
          ) : (
            <>
              Ready · analyzing <strong className="font-semibold text-foreground">{files.length}</strong>{" "}
              contracts across <strong className="font-semibold text-foreground">11</strong> dimensions
            </>
          )}
        </p>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" disabled={!canAnalyze} onClick={() => setStage("parsing")}>
          <Sparkles className="size-3.5" />
          Analyze & compare
        </Button>
      </footer>
    </div>
  );
}

function DealComparisonParseScreen({
  files,
  onDone,
  onCancel,
}: {
  files: SampleUploadFile[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [tick, setTick] = useState(0);
  const totalTicks = files.length * DC_PARSE_STEPS.length + 4;
  const progress = Math.min(tick / totalTicks, 1);
  const allDone = tick >= totalTicks;

  useEffect(() => {
    const t = window.setInterval(() => setTick((x) => x + 1), 90);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (tick >= totalTicks + 1) {
      const timer = window.setTimeout(() => onDone(), 150);
      return () => window.clearTimeout(timer);
    }
  }, [tick, totalTicks, onDone]);

  const stepsPerFile = (i: number) => {
    const start = i * 0.6;
    const fileTick = Math.max(0, tick - start);
    return Math.min(Math.floor(fileTick / 0.6), DC_PARSE_STEPS.length);
  };

  const synthesisStep = Math.max(0, tick - files.length * DC_PARSE_STEPS.length);
  const synthesisItems = [
    "Aligning extracted terms across all offers",
    "Computing cross-offer deltas & winner per dimension",
    "Calculating composite scores",
    "Drafting summary for Travis",
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-6 py-3.5">
        <div className="flex size-8 items-center justify-center rounded-md border border-border bg-card">
          {allDone ? (
            <Check className="size-4 text-success" />
          ) : (
            <span className="size-3.5 animate-spin rounded-full border-2 border-muted border-t-primary" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-semibold text-primary">
            Deal Comparison · Analyzing
          </p>
          <p className="text-sm font-semibold">
            {allDone ? "Comparison ready" : `Reading ${files.length} contracts in parallel`}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </header>

      <div className="shrink-0 px-6 pt-3.5">
        <div className="mb-1.5 flex justify-between text-[11px]">
          <span className="text-muted-foreground">Overall progress</span>
          <span className="font-mono font-medium">{Math.round(progress * 100)}%</span>
        </div>
        <Progress value={progress * 100}>
          <ProgressTrack>
            <ProgressIndicator />
          </ProgressTrack>
        </Progress>
      </div>

      <div className="flex-1 overflow-auto px-6 py-5">
        <div className="mx-auto max-w-3xl space-y-2.5">
          {files.map((f, i) => {
            const offer = DC_OFFERS.find((o) => o.id === f.offerId);
            const completed = stepsPerFile(i);
            const fileProgress = completed / DC_PARSE_STEPS.length;
            const isDone = completed === DC_PARSE_STEPS.length;
            const isActive = !isDone && tick >= i * 1.2;

            return (
              <Card key={f.id} className="p-3.5">
                <div className="mb-2 flex items-center gap-2.5">
                  {offer ? (
                    <AriBrandAvatar brandId={offer.id as AriBrandId} size="md" className="rounded-md" />
                  ) : (
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                      <FileText className="size-3 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold">{f.name}</p>
                    <p className="text-[10.5px] text-muted-foreground">
                      {isDone
                        ? "Parsed · all terms extracted"
                        : isActive
                          ? `${DC_PARSE_STEPS[Math.min(completed, DC_PARSE_STEPS.length - 1)]}…`
                          : "Queued"}
                    </p>
                  </div>
                  {isDone ? (
                    <span className="flex items-center gap-1 text-[10.5px] font-semibold text-success">
                      <Check className="size-3" />
                      Done
                    </span>
                  ) : isActive ? (
                    <span className="font-mono text-[11px] font-medium text-primary">
                      {Math.round(fileProgress * 100)}%
                    </span>
                  ) : (
                    <span className="text-[10.5px] text-muted-foreground">Queued</span>
                  )}
                </div>
                <Progress value={fileProgress * 100} variant={isDone ? "success" : "primary"}>
                  <ProgressTrack className="h-0.5">
                    <ProgressIndicator />
                  </ProgressTrack>
                </Progress>
                <div className="mt-2 flex flex-wrap gap-1">
                  {DC_PARSE_STEPS.map((s, si) => {
                    const stepDone = si < completed;
                    const stepActive = si === completed && isActive;
                    return (
                      <Badge
                        key={s}
                        variant={stepDone || stepActive ? "default" : "outline"}
                        className="text-[10px] font-normal"
                      >
                        {stepDone && <Check className="size-2" />}
                        {s}
                      </Badge>
                    );
                  })}
                </div>
              </Card>
            );
          })}

          <Alert className="border-primary/30 bg-primary/5">
            <Sparkles className="size-4 text-primary" />
            <div>
              <p className="text-[11.5px] font-semibold text-primary">
                Synthesis
              </p>
              <ul className="mt-2 space-y-1.5">
                {synthesisItems.map((s, i) => {
                  const done = synthesisStep > i;
                  const active = synthesisStep === i + 1;
                  return (
                    <li
                      key={s}
                      className={cn(
                        "flex items-center gap-2 text-xs",
                        done
                          ? "text-foreground"
                          : active
                            ? "text-muted-foreground"
                            : "text-muted-foreground/70",
                      )}
                    >
                      {done ? (
                        <Check className="size-3 text-primary" />
                      ) : active ? (
                        <span className="size-1.5 animate-pulse rounded-full bg-primary" />
                      ) : (
                        <span className="size-1 rounded-full bg-border" />
                      )}
                      {s}
                    </li>
                  );
                })}
              </ul>
            </div>
          </Alert>

          {allDone && (
            <Card className="flex-row items-center gap-3 border-primary/30 p-4">
              <div className="flex size-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Check className="size-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">All {files.length} contracts parsed.</p>
                <p className="text-[11.5px] text-muted-foreground">Opening side-by-side comparison…</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
